import * as React from 'react';
import toast from 'react-hot-toast';
import { FileUploadDropArea } from '@/components/shared/FileUploadDropArea';

const DOCX_ACCEPT = '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const isDocxFile = (file: File) => (
  file.name.toLowerCase().endsWith('.docx')
    || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
);

interface TemplateUploadDropAreaProps {
  disabled?: boolean;
  file: File | null;
  inputId?: string;
  onFileChange: (file: File | null) => void;
}

export const TemplateUploadDropArea = ({
  disabled = false,
  file,
  inputId,
  onFileChange,
}: TemplateUploadDropAreaProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dragCounterRef = React.useRef(0);
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);

  const chooseFile = React.useCallback((nextFile: File | null) => {
    if (!nextFile) {
      onFileChange(null);
      return;
    }
    if (!isDocxFile(nextFile)) {
      toast.error('Choose a DOCX template file');
      return;
    }
    onFileChange(nextFile);
  }, [onFileChange]);

  const handleDrop = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = 0;
    setIsDraggingOver(false);
    if (disabled) return;
    chooseFile(event.dataTransfer.files?.[0] ?? null);
  }, [chooseFile, disabled]);

  return (
    <>
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept={DOCX_ACCEPT}
        className="hidden"
        disabled={disabled}
        onChange={(event) => chooseFile(event.currentTarget.files?.[0] ?? null)}
      />
      <FileUploadDropArea
        disabled={disabled}
        disabledText="Uploading in progress..."
        dropZoneText={file ? file.name : 'Drag template here'}
        isDraggingOver={isDraggingOver}
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (disabled) return;
          dragCounterRef.current += 1;
          setIsDraggingOver(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!disabled) {
            event.dataTransfer.dropEffect = 'copy';
          }
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
          if (dragCounterRef.current === 0) {
            setIsDraggingOver(false);
          }
        }}
        onDrop={handleDrop}
        onSelectFiles={() => inputRef.current?.click()}
        selectFilesButtonText={file ? 'Replace file' : 'Select File'}
        showSelectFolderButton={false}
        supportedTypesText={file ? 'DOCX selected' : 'DOCX only'}
      />
    </>
  );
};
